// TODO(auth): drop this module and route submit() back through
// api.me.submitPersonality(...) once /me/* endpoints are guarded by a real
// session. Until then this is the local-only mirror of the backend's
// PersonalityService.score() + fallbackNarrative() so the test completes
// without any server round-trip.

import type { Locale } from '@llura/constants';
import type {
  BigFiveAnswer,
  BigFiveQuestionId,
  BigFiveScores,
  BigFiveTrait,
} from '@llura/types';

import { BIG_FIVE_QUESTIONS } from './questions';

const TRAITS: readonly BigFiveTrait[] = [
  'openness',
  'conscientiousness',
  'extraversion',
  'agreeableness',
  'neuroticism',
];

/**
 * TIPI-style: each trait has one forward and one reverse item. Forward stays,
 * reverse becomes 6 − value, average the two, then linearly stretch 1–5 → 0–100.
 * 1:1 port of PersonalityService.score() in apps/api.
 */
export function scoreAnswers(answers: BigFiveAnswer[]): BigFiveScores {
  const byId = new Map<BigFiveQuestionId, number>(
    answers.map((a) => [a.questionId, a.value]),
  );
  const result = {} as BigFiveScores;
  for (const trait of TRAITS) {
    const items = BIG_FIVE_QUESTIONS.filter((q) => q.trait === trait);
    const aligned = items.map((q) => {
      const v = byId.get(q.id) ?? 3;
      return q.direction === 'forward' ? v : 6 - v;
    });
    const mean = aligned.reduce((a, b) => a + b, 0) / aligned.length;
    result[trait] = Math.round(((mean - 1) / 4) * 100);
  }
  return result;
}

/**
 * Deterministic 3-sentence portrait — 1:1 port of
 * PersonalityService.fallbackNarrative(). No LLM call; safe to run offline.
 */
export function fallbackNarrative(scores: BigFiveScores, locale: Locale): string {
  const hi = (n: number) => n >= 60;
  const lo = (n: number) => n <= 40;
  if (locale === 'de') {
    const parts: string[] = [];
    parts.push(
      hi(scores.openness)
        ? 'Du suchst die Ränder, das Unbekannte — wer dich führt, darf dich überraschen.'
        : lo(scores.openness)
          ? 'Du fühlst dich in vertrauten Rhythmen am sichersten — Verlässlichkeit ist dein Aphrodisiakum.'
          : 'Du bleibst neugierig, ohne dich zu verlieren.',
    );
    parts.push(
      hi(scores.conscientiousness)
        ? 'Klare Strukturen und Regeln erregen dich; Ordnung ist für dich eine Form von Geborgenheit.'
        : lo(scores.conscientiousness)
          ? 'Das Ungeplante zieht dich an — du blühst in spontanen Momenten auf.'
          : 'Du magst Halt, aber lässt Raum für das Unerwartete.',
    );
    parts.push(
      hi(scores.neuroticism)
        ? 'Dein Begehren wechselt schnell — wer dich führt, muss feinfühlig zwischen Hunger und Zögern lesen können.'
        : 'Du bleibst emotional ruhig, egal wie zart oder intensiv der Moment ist.',
    );
    return parts.join(' ');
  }
  const parts: string[] = [];
  parts.push(
    hi(scores.openness)
      ? 'You want the edges, the unfamiliar — whoever leads you is allowed to surprise you.'
      : lo(scores.openness)
        ? 'You feel safest in the rhythms you already know — reliability is its own aphrodisiac for you.'
        : 'You stay curious without losing yourself.',
  );
  parts.push(
    hi(scores.conscientiousness)
      ? 'Clear rules and structure turn you on; order is a form of intimacy for you.'
      : lo(scores.conscientiousness)
        ? 'The unplanned pulls you in — you come alive in spontaneous moments.'
        : 'You like structure but keep room for the unexpected.',
  );
  parts.push(
    hi(scores.neuroticism)
      ? 'Your desire shifts fast — whoever leads you needs to read the line between hunger and hesitation with care.'
      : 'You stay emotionally steady whether the moment is tender or intense.',
  );
  return parts.join(' ');
}
